import { apiFetch, apiFetchV2 } from '@m/utils/http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { invalidateSiteLanesQuery } from './lanes';
import { getLaneDetailQueryKey, getLaneGateDetailQueryKey } from './query-keys';
import { adminSiteLaneGate, adminSiteLaneGates, adminSiteLaneSignCode } from './routes';

import type {
  CustomerInitiatedAccess,
  GateActions,
  GateStatus,
  LocationLaneDetail,
} from '@m/types/api';
import type { APIResponse } from '@m/utils/http';

type LaneGateDetail = LaneGatePayload & {
  edgeGateView?: LocationLaneDetail['edgeDevice'];
  gateActions?: GateActions;
  gateLaneView?: GateStatus;
};

type LaneGatePayload = {
  controlDefaults: {
    pulseDelayMs: number;
    pulseLengthMs: number;
  };
  deviceRelayNum: number;
  deviceUuid: string;
  enabled: boolean;
  gateActions: {
    automatic: boolean;
    customerInitiatedAccess?: CustomerInitiatedAccess;
    nestedZone: boolean;
    parkingPass: boolean;
    reservation: boolean;
    subscriber: boolean;
    textToPay: boolean;
    textToVend: boolean;
    valetExit: boolean;
    vendOnSuccessfulCharge: boolean;
  };
  laneId: number;
  notes: string;
};

type AddLaneGateMutationVariables = {
  laneId: number;
  payload: LaneGatePayload;
  siteId: number;
};

type UpdateLaneGateMutationVariables = {
  gateId: number;
  laneId: number;
  payload: LaneGatePayload;
  siteId: number;
};

type RemoveLaneGateMutationVariables = {
  gateId: number;
  laneId: number;
  siteId: number;
};

const getLaneGateDetail = async ({
  locationId,
  laneId,
}: {
  laneId: number;
  locationId: number;
}): Promise<LaneGateDetail | null> => {
  // TODO: Look into adding 404 logic to apiFetchV2.
  // apiFetch used intentionally: we need to treat 404 as success and return null; apiFetchV2 throws on any non-200.
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- 404 must be handled as success; apiFetchV2 throws
  const response = (await apiFetch<LaneGateDetail>(
    adminSiteLaneGates(locationId, laneId),
  )) as APIResponse<LaneGateDetail> & { status?: number };
  if (response.status === 404) return null;
  if (!response.success) {
    throw new Error(response.statusText ?? response.error);
  }
  return response.data ?? null;
};

const addLaneGate = ({ payload, siteId, laneId }: AddLaneGateMutationVariables) =>
  apiFetchV2(adminSiteLaneGates(siteId, laneId), {
    method: 'POST',
    body: payload,
  });

const updateLaneGate = ({ payload, siteId, laneId, gateId }: UpdateLaneGateMutationVariables) =>
  apiFetchV2(adminSiteLaneGate(siteId, laneId, gateId), {
    method: 'PUT',
    body: payload,
  });

const removeLaneGate = ({ siteId, laneId, gateId }: RemoveLaneGateMutationVariables) =>
  apiFetchV2(adminSiteLaneGate(siteId, laneId, gateId), {
    method: 'DELETE',
  });

export const useLaneGateDetail = (locationId: number, laneId: number) =>
  useQuery({
    queryKey: getLaneGateDetailQueryKey(locationId, laneId),
    queryFn: () => getLaneGateDetail({ locationId, laneId }),
    enabled: !!locationId && !!laneId,
  });

export const useAddLaneGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addLaneGate,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLaneDetailQueryKey(variables.siteId, variables.laneId),
      });
      queryClient.invalidateQueries({
        queryKey: getLaneGateDetailQueryKey(variables.siteId, variables.laneId),
      });
      invalidateSiteLanesQuery(queryClient, String(variables.siteId));
    },
  });
};

export const useUpdateLaneGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLaneGate,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLaneDetailQueryKey(variables.siteId, variables.laneId),
      });
      queryClient.invalidateQueries({
        queryKey: getLaneGateDetailQueryKey(variables.siteId, variables.laneId),
      });
      invalidateSiteLanesQuery(queryClient, String(variables.siteId));
    },
  });
};

export const useRemoveLaneGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeLaneGate,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLaneDetailQueryKey(variables.siteId, variables.laneId),
      });
      queryClient.invalidateQueries({
        queryKey: getLaneGateDetailQueryKey(variables.siteId, variables.laneId),
      });
      invalidateSiteLanesQuery(queryClient, String(variables.siteId));
    },
  });
};

const generateLaneSignCode = ({ siteId, laneId }: { laneId: number; siteId: number }) =>
  apiFetchV2<{ signCode: string }>(adminSiteLaneSignCode(siteId, laneId), {
    method: 'POST',
  });

export const useGenerateLaneSignCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generateLaneSignCode,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLaneDetailQueryKey(variables.siteId, variables.laneId),
      });
      queryClient.invalidateQueries({
        queryKey: getLaneGateDetailQueryKey(variables.siteId, variables.laneId),
      });
      invalidateSiteLanesQuery(queryClient, String(variables.siteId));
    },
  });
};
